import noop from 'lodash/noop';
import UAParser from 'ua-parser-js';

import { StatsPageViewNames } from '../constants';
import { mapPathToPageView } from '../utils';

import {
  ConcertEventActionFromTypes,
  DefaultFieldNames,
  DefaultValues,
  Events,
  ListenerTypes,
  PerformanceKeys,
  SearchKeys,
  StatsStoreKeys,
  TTMAudioTypes,
  TunerVarFieldNames,
  ViewModes,
} from './constants';
import * as D from './deps';
import { CoachmarkIds } from './externals';

const logger = D.Logger.child({ component: 'stats/utils' });
const uaParser = new UAParser();
const browserName = uaParser.getBrowser().name;
const browserOS = uaParser.getOS().name;
let StatsUtilsModule = {};

export function getSearchDataFromState(state) {
  return {
    searchTerm: state.search.get('searchTerm'),
  };
}

export function getSODSearchDataFromState(state) {
  return {
    searchTerm: state.searchT3.get('mainSearch').get('searchTerm'),
  };
}

export function mapToHiveValues() {
  return {
    // https://wiki.savagebeast.com/pages/viewpage.action?pageId=63278975&flashId=1113232472
    [D.Constants.TrackType.TrackType.TRACK]: 'track',
    [D.Constants.TrackType.TrackType.ARTIST_MESSAGE]: 'artist_message',
    [D.Constants.TrackType.TrackType.AUDIO_WARNING]: 'audio_warning',
    [D.Constants.TrackType.TrackType.AUDIO_INTRO_MESSAGE]: 'station_introduction',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_REQUEST]: 'request',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_RESPONSE]: 'response',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_ERROR_RESPONSE]: 'error_response',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_START_RENDER]: 'start_render',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_FINISH_RENDER]: 'finish_render',
    [D.Constants.Analytics.ActionTypes.WEB_DISPLAY_AD_DISMISSED]: 'dismissed',
  };
}

export function getCoachmarkId(sourceType, openedFromSearch) {
  const coachmarkIds = {
    [D.Constants.SourceTypes.PandoraTypes.ALBUM]: CoachmarkIds.ALBUM_BACKSTAGE,
    [D.Constants.SourceTypes.PandoraTypes.ARTIST]: CoachmarkIds.ARTIST_BACKSTAGE,
    [D.Constants.SourceTypes.PandoraTypes.SONG]: CoachmarkIds.TRACK_BACKSTAGE,
    [D.Constants.SourceTypes.PandoraTypes.PLAYLIST]: CoachmarkIds.PLAYLIST_BACKSTAGE,
  };
  return openedFromSearch ? CoachmarkIds.TRACK_SEARCH : coachmarkIds[sourceType];
}

export function getSearchSessionDataFromState(state) {
  const currentSearchSessionId = state.stats
    .get(StatsStoreKeys.SEARCH)
    .get(SearchKeys.CURRENT_SEARCH_SESSION_ID);
  const currentSearchSequenceNumber = state.stats
    .get(StatsStoreKeys.SEARCH)
    .get(SearchKeys.CURRENT_SEARCH_SEQUENCE_NUMBER);

  return {
    currentSearchSessionId,
    currentSearchSequenceNumber,
  };
}

export function getBrowseSessionId(state) {
  return state.stats.get(StatsStoreKeys.BROWSE_SESSION_ID);
}

export function getDisplayAdRequestDataFromState(state) {
  const correlationId = D.getInDomain('displayAds.utils.getCorrelationId', noop)(state);
  const adUrl = D.getInDomain('displayAds.utils.getDisplayAdUrl', noop)(state);

  return {
    correlationId,
    adUrl,
  };
}

export function getCreativeAndLineIdsFromState(state) {
  const externalAdIdentifiers = D.getInDomain('ads.utils.getExternalAdIdentifiers')(state).toJS();
  const creativeId = externalAdIdentifiers.creativeId;
  const lineId = externalAdIdentifiers.lineId;
  return {
    creativeId,
    lineId,
  };
}

export function getVendorId() {
  let vendorId = DefaultValues.STATS_VENDOR_ID;

  if (D.Utils.ClientInfo.isAndroidClient()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_ANDROID;
  } else if (D.Utils.ClientInfo.isIOSClient()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_IOS;
  } else if (D.Utils.ClientInfo.isElectron()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_DESKTOP_APP;
  }

  return vendorId;
}

export function getDefaultEventData(state) {
  const now = D.Utils.Time.getNow().toString();
  const pathName = D.Utils.Route.getCurrentPathname({
    location: state.routing.locationBeforeTransitions,
  });
  const { correlationId, partnerId } = StatsUtilsModule.getInboundTrackingDataFromState(state);
  let vendorId = DefaultValues.STATS_VENDOR_ID;
  if (D.Utils.ClientInfo.isAndroidClient()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_ANDROID;
  } else if (D.Utils.ClientInfo.isIOSClient()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_IOS;
  } else if (D.Utils.ClientInfo.isElectron()) {
    vendorId = DefaultValues.STATS_VENDOR_ID_DESKTOP_APP;
  }
  const listenerId = D.Utils.Listener.getCurrentListenerIdFromState(state);

  return {
    [DefaultFieldNames.APP_VERSION]: D.Utils.DebugSettings.version(),
    [DefaultFieldNames.BROWSER_ID]: browserName,
    [DefaultFieldNames.BROWSER]: browserName,
    [DefaultFieldNames.BROWSER_VERSION]: D.Utils.ClientInfo.getBrowserVersion(),
    [DefaultFieldNames.CLIENT_TIMESTAMP]: now,
    [DefaultFieldNames.DATE_RECORDED]: now,
    [DefaultFieldNames.DAY]: D.Utils.DateUtils.formatStatsDayDate(new Date()),
    [DefaultFieldNames.DEVICE_CODE]: D.getInDomain('application.utils.getDeviceId', '')(state),
    [DefaultFieldNames.DEVICE_ID]: D.getInDomain('application.utils.getDeviceId', '')(state),
    [DefaultFieldNames.DEVICE_UUID]: D.getInDomain('application.utils.getDeviceUuid', '')(state),
    [DefaultFieldNames.DEVICE_OS]: browserOS,
    [DefaultFieldNames.IS_ON_DEMAND_USER]: D.Utils.Listener.hasPremiumFeatures(state).toString(),
    [DefaultFieldNames.LINK_CORRELATION_ID]: correlationId,
    [DefaultFieldNames.LINK_PARTNER_ID]: partnerId,
    [DefaultFieldNames.LISTENER_ID]: listenerId,
    [DefaultFieldNames.MUSIC_PLAYING]: D.getInDomain(
      'playback.utils.isAudioPlaying',
      false,
    )(state).toString(),
    [DefaultFieldNames.NOT_VISIBLE]: D.Utils.PageVisibility.isSupported()
      ? D.Utils.PageVisibility.isHidden().toString()
      : '',
    [DefaultFieldNames.PAGE_VIEW]: mapPathToPageView(state, pathName) || '',
    [DefaultFieldNames.SITE_VERSION]: D.Utils.DebugSettings.version(),
    [DefaultFieldNames.VENDOR_ID]: +vendorId,
    [DefaultFieldNames.SPECIAL_OFFER_CODE]: D.Selectors.Payments.getSpecialOfferCode(state),
    [DefaultFieldNames.SPECIAL_OFFER_CAMPAIGN_ID]:
      +D.Selectors.Payments.getSpecialOfferCampaignId(state),
    [DefaultFieldNames.TUNER_VAR_FLAGS]: D.Utils.TunerVars.getTunerVarAnalyticsFlags(state),
  };
}

export function getBrowserDimensions() {
  const win = window;
  const doc = document;
  const el = doc.documentElement;
  const bodyElement = doc.getElementsByTagName('body')[0];
  const width = win.innerWidth || el.clientWidth || bodyElement.clientWidth;
  const height = win.innerHeight || el.clientHeight || bodyElement.clientHeight;
  return { height, width };
}

export function getAPSEventData(state) {
  const defaultData = StatsUtilsModule.getDefaultEventData(state);

  return {
    ...defaultData,
    [TunerVarFieldNames.ARTIST_COLLABORATIONS_ENABLED]: true,
  };
}

export function getDisplayAdEventData(state, eventName) {
  const defaultData = StatsUtilsModule.getDefaultEventData(state);
  const browserResolution = getBrowserDimensions();

  const pathName = D.Utils.Route.getCurrentPathname({
    location: state.routing.locationBeforeTransitions,
  });

  const { correlationId, adUrl } = getDisplayAdRequestDataFromState(state);

  const { creativeId, lineId } = getCreativeAndLineIdsFromState(state);

  // Get interaction from state
  const action = D.getInDomain('displayAds.utils.getDisplayAdInteraction', noop)(state);
  const eventText = mapToHiveValues()[eventName];

  const displayAdEventData = {
    [DefaultFieldNames.ACTION]: action,
    [DefaultFieldNames.AD_DISPLAY_TYPE]: 'display',
    [DefaultFieldNames.CORRELATION_ID]: correlationId || '',
    [DefaultFieldNames.REQUEST_PARAMS]: adUrl || '',
    [DefaultFieldNames.AD_PLACEMENT]: mapPathToPageView(state, pathName) || '',
    [DefaultFieldNames.EVENT]: eventText || eventName || '',
    [DefaultFieldNames.CREATIVE_ID]: creativeId || '',
    [DefaultFieldNames.LINE_ID]: lineId || '',
    [DefaultFieldNames.BROWSER_WIDTH]: browserResolution.width || '',
    [DefaultFieldNames.BROWSER_HEIGHT]: browserResolution.height || '',
  };

  Object.assign(defaultData, displayAdEventData);

  return defaultData;
}

export function getPandoraIdFromStartupQueryParams(state) {
  return state.application
    .get(D.Constants.ApplicationStateKeys.ApplicationStateKeys.STARTUP_QUERY_PARAMETERS)
    .get('pandoraId');
}

export function getInboundTrackingDataFromState(state) {
  const rawUrl = state.application
    .get(D.Constants.ApplicationStateKeys.ApplicationStateKeys.STARTUP_QUERY_PARAMETERS)
    .get('rawUrl');
  const pandoraId = state.application
    .get(D.Constants.ApplicationStateKeys.ApplicationStateKeys.STARTUP_QUERY_PARAMETERS)
    .get('pandoraId');
  const partnerId = state.application
    .get(D.Constants.ApplicationStateKeys.ApplicationStateKeys.STARTUP_QUERY_PARAMETERS)
    .get('part');
  const correlationId = state.application
    .get(D.Constants.ApplicationStateKeys.ApplicationStateKeys.STARTUP_QUERY_PARAMETERS)
    .get('corr');

  return {
    rawUrl,
    pandoraId,
    partnerId,
    correlationId,
  };
}

export function getConcertActionFromField(state) {
  const pathname = D.Utils.Route.getCurrentPathnameFromState(state);
  let actionFrom;

  // set actionFrom depending on the page
  if (
    D.Utils.Route.isPathInNowPlayingPath(pathname) &&
    D.Utils.UIState.getIsSessionHistoryOpen(state)
  ) {
    actionFrom = ConcertEventActionFromTypes.HISTORY;
  } else if (D.Utils.Route.isPathInNowPlayingPath(pathname)) {
    actionFrom = ConcertEventActionFromTypes.NOW_PLAYING;
  } else if (D.Utils.Route.isBackstageArtistPath(pathname)) {
    actionFrom = ConcertEventActionFromTypes.ARTIST_PAGE;
  } else {
    logger.error(`No action_from found for event on ${pathname}`);
  }
  return actionFrom;
}

export function getConcertEventData(state, actionData) {
  const stationId = D.getInDomain('playback.utils.getCurrentStationId', null)(state);
  const actionFrom = getConcertActionFromField(state);

  return {
    action_from: actionFrom,
    action_link: actionData.actionLink,
    artist_id: actionData.artistId,
    event_id: actionData.eventId,
    notification_id: +actionData.notificationId,
    station_id: +stationId,
  };
}

export function getEvents(state) {
  return state.stats.get(StatsStoreKeys.EVENTS);
}

export function createTimeToMusicStatsObject(
  timeToMusic,
  ttmAction,
  audioType = TTMAudioTypes.MUSIC,
) {
  return {
    eventName: Events.TIME_TO_MUSIC,
    eventData: {
      [PerformanceKeys.TTM_MSEC]: timeToMusic,
      [PerformanceKeys.TTM_ACTION]: ttmAction,
      [PerformanceKeys.TTM_AUDIO_TYPE]: audioType,
    },
  };
}

export function createTimeToMusicDefaultEventData(defaultEventData) {
  // Map our default event data to the event_time_to_music schema.
  return {
    accessory_id: null,
    app_version: defaultEventData[DefaultFieldNames.APP_VERSION],
    browser_version: defaultEventData[DefaultFieldNames.BROWSER_VERSION],
    client_ip: null,
    client_timestamp: defaultEventData[DefaultFieldNames.CLIENT_TIMESTAMP],
    date_recorded: defaultEventData[DefaultFieldNames.DATE_RECORDED],
    day: defaultEventData[DefaultFieldNames.DAY],
    device_code: defaultEventData[DefaultFieldNames.DEVICE_CODE],
    device_model: defaultEventData[DefaultFieldNames.BROWSER],
    device_os: defaultEventData[DefaultFieldNames.DEVICE_OS],
    listener_id: defaultEventData[DefaultFieldNames.LISTENER_ID],
    pandora_link: null,
    vendor_id: defaultEventData[DefaultFieldNames.VENDOR_ID],
  };
}

export function createBufferingStatsObject(bufferingTime, action, audioType = TTMAudioTypes.MUSIC) {
  return {
    eventName: Events.EVENT_LISTENER_BUFFERING,
    eventData: {
      [PerformanceKeys.BUFFERING_MSEC]: bufferingTime,
      [PerformanceKeys.TTM_ACTION]: action,
      [PerformanceKeys.TTM_AUDIO_TYPE]: audioType,
    },
  };
}

export function getBufferingDefaultEventData(state) {
  const defaultValues = getDefaultEventData(state);
  return {
    view_mode: ViewModes.STANDARD,
    is_casting: null,
    is_offline: null,
    ...defaultValues,
  };
}

export function getBrowsePageView(path) {
  let eventData = null;
  if (!path) {
    return eventData;
  }

  const rootPath = D.Utils.Route.getBrowsePath();
  const pageViews = [
    // NOTE: use ^ and $ for all regular expressions for precise matching
    {
      regExp: new RegExp(`^${rootPath}$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_MAIN,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_HOME,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/featured-playlists$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_FEATURED_PLAYLISTS,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/top-stations$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_TOP_STATIONS,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/top-artists$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_TOP_ARTISTS,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/artists-on-the-rise$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_ARTISTS_ON_THE_RISE,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/new-music$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_NEW_MUSIC,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/recommended-artists$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_RECOMMENDED_ARTISTS,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/activities$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_MOODS_AND_ACTIVITIES,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/activities/.+$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_MOODS_AND_ACTIVITIES_CATEGORY,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/genres$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_GENRES,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/genres/.+$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_GENRES_CATEGORY,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/decades$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_DECADES,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/music/decades/.+$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_DECADES_CATEGORY,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/podcasts$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_PODCASTS,
      },
    },
    {
      regExp: new RegExp(`^${rootPath}/podcasts/.+$`),
      eventData: {
        [DefaultFieldNames.PAGE_VIEW]: StatsPageViewNames.BROWSE_DEEP,
        [DefaultFieldNames.VIEW_MODE]: ViewModes.BROWSE_PODCASTS_CATEGORY,
      },
    },
  ];
  const matchingPageView = pageViews.find(pageView => pageView.regExp.test(path));
  if (matchingPageView && matchingPageView.eventData) {
    eventData = matchingPageView.eventData;
  }

  return eventData;
}

export function createPartnerUpgradeEventData(state) {
  const isListenerLoggedIn = D.Utils.Listener.isLoggedIn(state);
  const isAnonymousListener = D.Utils.Listener.isAnonymousListener(state);
  const listenerType = isAnonymousListener
    ? ListenerTypes.ANON
    : isListenerLoggedIn
    ? ListenerTypes.AUTHENTICATED
    : ListenerTypes.UNAUTHENTICATED;
  return {
    sku_feature_code: D.Selectors.Payments.getActiveProductGroupTier(state),
    sku_product_type: D.Selectors.Payments.getActiveProductType(state),
    sku_store: D.Selectors.Payments.getPaymentProvider(state),
    listenerType,
  };
}

export function getPartnerNamePrefix(state) {
  const partnerUpgradePartnerName = D.Selectors.Payments.getPartnerUpgradePartnerName(state);
  // Special characters are safe to pass to the stats tables as string fields
  return `${partnerUpgradePartnerName.toUpperCase()}_`;
}

export function isBackstageContentSection(contentSection) {
  return Object.values(D.Constants.ContentSections.BackstageContentSections).includes(
    contentSection,
  );
}

/**
 * Export Module
 */

StatsUtilsModule = {
  getDefaultEventData,
  getInboundTrackingDataFromState,
  getBrowsePageView,
};

export default StatsUtilsModule;
